import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Trash2, Plus, X } from 'lucide-react';
import { listUsers, deleteUser, createUsersBulk } from '@/DB/CRUD';
import { User, NewUserInput, BulkResult } from '@/lib/constants';

interface UserRow extends NewUserInput {
  role: 'scanner' | 'admin';
}

const emptyRow = (): UserRow => ({ username: '', email: '', password: '', role: 'scanner' });

function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [newUsers, setNewUsers] = useState<UserRow[]>([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await listUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (userId: string) => {
    try {
      await deleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const openModal = () => {
    setNewUsers([emptyRow()]);
    setBulkResult(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setBulkResult(null);
    if (bulkResult && bulkResult.created.length > 0) {
      fetchUsers();
    }
  };

  const updateRow = (index: number, field: keyof UserRow, value: string) => {
    setNewUsers((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => setNewUsers((prev) => [...prev, emptyRow()]);

  const removeRow = (index: number) => {
    setNewUsers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    const filled = newUsers.filter((u) => u.username.trim() || u.email.trim());
    if (filled.length === 0) return;

    setSubmitting(true);
    try {
      const result = await createUsersBulk(filled);
      setBulkResult(result);
      if (result.created.length > 0) {
        fetchUsers();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create users');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const inputClass =
    'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#27ae60] focus:border-[#27ae60]';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Create Users Modal */}
      {showModal && (
        <div role="dialog" aria-modal="true" aria-labelledby="modal-title" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 id="modal-title" className="text-xl font-bold text-gray-800">Create Users</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {bulkResult ? (
                /* Results view */
                <div className="space-y-3">
                  {bulkResult.created.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <p className="text-green-700 font-semibold mb-1">
                        {bulkResult.created.length} user{bulkResult.created.length !== 1 ? 's' : ''} created successfully
                      </p>
                      <ul className="text-sm text-green-600 space-y-0.5">
                        {bulkResult.created.map((u) => (
                          <li key={u.id}>• {u.username} ({u.email})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {bulkResult.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="text-red-700 font-semibold mb-1">
                        {bulkResult.errors.length} user{bulkResult.errors.length !== 1 ? 's' : ''} failed
                      </p>
                      <ul className="text-sm text-red-600 space-y-0.5">
                        {bulkResult.errors.map((e, i) => (
                          <li key={i}>• {e.username}: {e.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                /* Entry form */
                <div className="space-y-3">
                  {/* Header row */}
                  <div className="grid grid-cols-[1fr_1fr_1fr_120px_32px] gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
                    <span>Username</span>
                    <span>Email</span>
                    <span>Password</span>
                    <span>Role</span>
                    <span />
                  </div>
                  {newUsers.map((row, index) => (
                    <div key={index} className="grid grid-cols-[1fr_1fr_1fr_120px_32px] gap-2 items-center">
                      <input
                        type="text"
                        value={row.username}
                        onChange={(e) => updateRow(index, 'username', e.target.value)}
                        placeholder="username"
                        className={inputClass}
                      />
                      <input
                        type="email"
                        value={row.email}
                        onChange={(e) => updateRow(index, 'email', e.target.value)}
                        placeholder="email@example.com"
                        className={inputClass}
                      />
                      <input
                        type="password"
                        value={row.password}
                        onChange={(e) => updateRow(index, 'password', e.target.value)}
                        placeholder="password"
                        className={inputClass}
                      />
                      <select
                        value={row.role}
                        onChange={(e) => updateRow(index, 'role', e.target.value)}
                        className={inputClass}
                      >
                        <option value="scanner">Scanner</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        onClick={() => removeRow(index)}
                        disabled={newUsers.length === 1}
                        className="text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={addRow}
                    className="flex items-center gap-1 text-sm text-[#27ae60] hover:underline mt-1"
                  >
                    <Plus className="h-4 w-4" /> Add another user
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <Button variant="outline" onClick={closeModal}>
                {bulkResult ? 'Close' : 'Cancel'}
              </Button>
              {!bulkResult && (
                <Button
                  onClick={handleCreate}
                  disabled={submitting}
                  className="bg-[#27ae60] hover:bg-[#229954] text-white"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Creating...
                    </span>
                  ) : (
                    'Create Users'
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <Card className="rounded-t-lg shadow-md m-5 border-0">
        <CardHeader className="bg-[#2c3e50] text-white py-10 px-5 flex flex-row justify-between items-center rounded-t-lg">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="secondary"
              className="bg-[#34495e] hover:bg-[#3d566e] text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <CardTitle className="text-4xl font-bold font-['Arial'] text-white m-0">
              User Management
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="p-5">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-between items-center mb-4">
            <CardTitle className="text-2xl font-bold font-['Arial']">Users</CardTitle>
            <Button
              onClick={openModal}
              className="bg-[#27ae60] hover:bg-[#229954] text-white"
            >
              <Plus className="h-4 w-4 mr-1" /> Create Users
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="hover:bg-[#34495e]">
                <TableHead className="bg-[#34495e] text-white">Username</TableHead>
                <TableHead className="bg-[#34495e] text-white">Email</TableHead>
                <TableHead className="bg-[#34495e] text-white">Role</TableHead>
                <TableHead className="bg-[#34495e] text-white">Created</TableHead>
                <TableHead className="bg-[#34495e] text-white w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell className="text-gray-600">{user.email}</TableCell>
                    <TableCell>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full uppercase font-semibold ${
                          user.role === 'admin'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="p-1.5 rounded text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default UserManagement;
